<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import IconNext from 'next/icon/Icon.vue';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import FileUpload from 'vue-upload-component';
import {
  isOnBotView,
  isOnCampaignView,
  isOnIntegrationHooks,
  isOnTaskView,
} from '../../../helpers/routeViewHelpers';
import { resolveVariablePath } from '../../helpers/variablePathResolver';
import VariablesDropdown from '../variablesDropdown/Index.vue';
import variablesMixins from '../variablesDropdown/variablesMixins';
import AttachmentPreview from './components/AttachmentsPreview.vue';
import { MAXIMUM_FILE_UPLOAD_SIZE } from './helpers/constants';

export default {
  components: {
    FileUpload,
    AttachmentPreview,
    ButtonNext,
    IconNext,
    VariablesDropdown,
  },
  mixins: [variablesMixins],
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    fallbackLabel: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      updatedValue: this.value || [],
    };
  },
  computed: {
    routeName() {
      return this.$route.name;
    },
    recordId() {
      if (isOnCampaignView(this.routeName)) {
        return this.$route.params.campaignId;
      }
      if (isOnTaskView(this.routeName)) {
        return this.$route.params.taskId;
      }
      if (isOnBotView(this.routeName)) {
        return this.$route.params.toolsetId;
      }
      if (isOnIntegrationHooks(this.routeName)) {
        const paths = this.$route.path.split('/');
        return paths[paths.length - 1];
      }

      return this.$route.params.automationId;
    },
    record() {
      if (isOnCampaignView(this.routeName)) {
        return this.$store.getters['wgptCampaigns/getCampaign'](this.recordId);
      }
      if (isOnTaskView(this.routeName)) {
        return this.$store.getters['wgptTasks/getTask'](this.recordId);
      }
      if (isOnBotView(this.routeName)) {
        const { toolSetId, toolId } = this.$route.params;
        return this.$store.getters['wgptBotTools/getTool'](toolSetId, toolId);
      }
      if (isOnIntegrationHooks(this.routeName)) {
        const integration = this.$store.getters['integrations/getIntegration'](
          this.recordId
        );
        return integration.hooks[0];
      }

      return this.$store.getters['wgptAutomations/getAutomation'](
        this.recordId
      );
    },
    recordFiles() {
      return this.record.files ?? [];
    },
    // The value can mix uploaded file ids (numbers) with %{...} variable
    // tokens (strings) when the field allows variables.
    fileIdEntries() {
      return this.updatedValue.filter(entry => typeof entry === 'number');
    },
    variableEntries() {
      return this.updatedValue.filter(entry => typeof entry === 'string');
    },
    attachedFiles() {
      return this.fileIdEntries.map(id => {
        const resource = this.recordFiles.find(file => file.id === id);
        return {
          id,
          resource: resource,
          thumb: resource?.file_url,
        };
      });
    },
    allowVariables() {
      return Boolean(this.field.allow_variables);
    },
    variableChips() {
      const resolvers = {
        getCustomObjects:
          this.$store.getters['wgptAutomationRegistry/getCustomObjects'],
        getDynamicOptions:
          this.$store.getters['wgptAutomationRegistry/getDynamicOptions'],
        toLocale: this.toLocale,
      };
      return this.variableEntries.map(token => {
        const match = token.match(/^[%#]\{(.+)\}$/);
        const path = match ? match[1] : token;
        const resolved = resolveVariablePath(this.variables, path, resolvers);
        const valid = resolved.status === 'resolved';
        const label = valid
          ? resolved.chain.map(node => node.resolvedLabel).join('/')
          : path;
        return { token, label, valid };
      });
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
  },
  validations() {
    return {
      updatedValue: {
        required: updatedValue => {
          if (!this.required) return true;

          return updatedValue.length > 0;
        },
      },
    };
  },
  watch: {
    value: {
      handler(value) {
        this.updatedValue = value || [];
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    updateValue() {
      this.v$.$touch();
      this.$emit('change', this.updatedValue);
    },
    async attachFile(event) {
      try {
        const file = event.file;
        if (!file) return;

        if (this.field.max && this.value.length >= this.field.max) {
          throw new Error('MAX_FILES_ERROR');
        }

        if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE))
          throw new Error('FILE_SIZE_ERROR');

        const data = {
          file,
          id: this.recordId,
        };
        const fileInfo = await this.addFile(data);

        this.updatedValue = [...this.updatedValue, fileInfo.id];
        this.updateValue();
      } catch (error) {
        if (error?.message === 'FILE_SIZE_ERROR') {
          useAlert(
            this.$t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.ATTACHMENT.API.FILE_SIZE_ERROR',
              {
                size: MAXIMUM_FILE_UPLOAD_SIZE,
              }
            )
          );
        } else if (error?.message === 'MAX_FILES_ERROR') {
          useAlert(
            this.$t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.ATTACHMENT.API.MAX_FILES_ERROR',
              {
                count: this.field.max,
              }
            )
          );
        } else {
          useAlert(
            this.$t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.ATTACHMENT.API.UPLOAD_ERROR_MESSAGE'
            )
          );
        }
      }
    },
    async removeAttachment(attachments) {
      try {
        const attachmentsIds = attachments.map(({ id }) => id);
        const entriesBefore = this.updatedValue ?? [];
        this.updatedValue = entriesBefore.filter(
          entry => typeof entry === 'string' || attachmentsIds.includes(entry)
        );
        this.updateValue();
      } catch (error) {
        useAlert(
          this.$t(
            'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.ATTACHMENT.API.REMOVE_ERROR_MESSAGE'
          )
        );
      }
    },
    onVariableAdd(_, variableName, variablePrefix = '%') {
      const token = `${variablePrefix}{${variableName}}`;
      if (this.updatedValue.includes(token)) return;

      this.updatedValue = [...this.updatedValue, token];
      this.updateValue();
    },
    removeVariable(token) {
      this.updatedValue = this.updatedValue.filter(entry => entry !== token);
      this.updateValue();
    },
    addFile(data) {
      if (isOnCampaignView(this.routeName)) {
        return this.$store.dispatch('wgptCampaigns/addFile', data);
      }
      if (isOnTaskView(this.routeName)) {
        return this.$store.dispatch('wgptTasks/addFile', data);
      }
      if (isOnBotView(this.routeName)) {
        const { botId, toolSetId, toolId } = this.$route.params;
        const addFileOptions = {
          botId: Number(botId),
          toolSetId: Number(toolSetId),
          id: Number(toolId),
          file: data.file,
        };
        return this.$store.dispatch('wgptBotTools/addFile', addFileOptions);
      }
      if (isOnIntegrationHooks(this.routeName)) {
        const addFileOptions = {
          id: this.record.id,
          automationId: this.record.automation_id,
          file: data.file,
        };
        return this.$store.dispatch('wgptIntegrations/addFile', addFileOptions);
      }

      return this.$store.dispatch('wgptAutomations/addFile', data);
    },
  },
};
</script>

<template>
  <div
    class="relative flex flex-col min-w-0 gap-1"
    :class="{ error: v$.updatedValue.$error }"
  >
    <label
      v-if="label || descriptionTooltip"
      class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
    >
      <div v-if="label" class="block truncate">
        {{ toLocale(label, fallbackLabel) }}<span v-if="required">*</span>
      </div>
      <IconNext
        v-if="descriptionTooltip"
        v-tooltip.top="descriptionTooltip"
        icon="i-lucide-info"
        class="text-n-slate-11 size-3.5"
      />
    </label>

    <div>
      <div v-if="hasAttachments" class="overflow-y-auto mb-4 mt-1.5">
        <AttachmentPreview
          class="!p-0"
          :attachments="attachedFiles"
          @update:attachments="removeAttachment"
        />
      </div>
      <div v-if="variableChips.length" class="flex flex-wrap gap-3 mb-4 mt-1.5">
        <div
          v-for="chip of variableChips"
          :key="chip.token"
          v-tooltip.top="
            chip.valid
              ? null
              : $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.VARIABLE_NOT_FOUND', {
                  variable: chip.token,
                })
          "
          class="max-w-[18.75rem] inline-flex items-center h-8 min-w-0 bg-n-alpha-2 dark:bg-n-solid-3 rounded-lg gap-2 ltr:pl-3 rtl:pr-3 ltr:pr-2 rtl:pl-2"
        >
          <IconNext
            icon="i-lucide-braces"
            class="shrink-0 size-3.5"
            :class="chip.valid ? 'text-n-slate-11' : 'text-n-amber-9'"
          />
          <span
            class="text-sm font-medium truncate"
            :class="chip.valid ? 'text-n-slate-11' : 'text-n-amber-9 italic'"
          >
            {{ chip.label }}
          </span>
          <ButtonNext
            variant="ghost"
            icon="i-lucide-x"
            color="slate"
            size="xs"
            class="shrink-0 !h-5 !w-5"
            @click="removeVariable(chip.token)"
          />
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <FileUpload
          :disabled="disabled"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          multiple
          drop
          :drop-directory="false"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          class="!text-start"
          @input-file="attachFile"
        >
          <ButtonNext
            :label="
              $t(
                'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.ATTACHMENT.SELECT'
              )
            "
            :disabled="disabled"
            icon="i-lucide-paperclip"
            size="sm"
            color="slate"
          />
        </FileUpload>
        <ButtonNext
          v-if="allowVariables"
          :label="
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.ATTACHMENT.ADD_VARIABLE'
            )
          "
          :disabled="disabled"
          icon="i-lucide-braces"
          size="sm"
          color="slate"
          @click="openVariablesDropdown"
        />
        <span class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.ATTACHMENT.HELP_TEXT'
            )
          }}
        </span>
      </div>
    </div>

    <VariablesDropdown
      v-if="allowVariables"
      :show-dropdown="showVariablesDropdown"
      :event="variablesDropdownEvent"
      :variables="variables"
      :filter-by-prefix="variablesFilter"
      filter-by-leaf-type="attachment"
      @close="closeVariablesDropdown"
      @add="addVariable"
    />

    <p
      v-if="v$.updatedValue.$error"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
    >
      {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.EMPTY') }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.button--only-icon ::v-deep * {
  @apply pointer-events-none;
}

.error input {
  @apply border-red-500;
}

:is(.dark .error input) {
  @apply border-red-500;
}
</style>
